import React from "react";
import styled from "styled-components";
import PropTypes from "prop-types";

import Stars from "./Stars";
import { optimizedImage, optimizedSrcSet } from "../../utils/cloudinaryImage";
import { CATALOG_ACTIONS, vendorTypeLabel } from "../../utils/cartRouting";
import useCatalogProductCart from "../../hooks/useCatalogProductCart";
import CrawlLink from "../CrawlLink";
import { formatCurrency } from "../../config/market";
import { isQuoteOnly, priceLabel } from "../../utils/priceOnRequest";

/* A /shop grid card (2 Nov 2026).
 *
 * Sells its product where the shopper is: "Add to cart" puts it in its
 * vendor's cart without leaving the grid and turns into a quantity control;
 * "See options" (a size to pick) and "Request quote" (price on request) open
 * the product's own page. The picture and the name open that page too, and
 * the vendor's name opens its storefront. The button used to read "Store" and
 * dropped the shopper on the vendor's storefront with the item they picked
 * nowhere in sight. */

// ── Vendor-type accent (solid colours, no gradients) ──
const VT_COLOR = {
  inhouse: "#03660C",
  retailer: "#2f80ed",
  wholesaler: "#8e44ad",
  brand: "#e08a1e",
};

const ACTION_GREEN = "#03660c";
const ACTION_GREEN_DARK = "#024f09";

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #eef1f2;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
  box-shadow: 0 1px 4px rgba(20, 40, 45, 0.08);
  transition: box-shadow 0.16s ease, transform 0.16s ease;
  &:hover {
    box-shadow: 0 10px 26px rgba(20, 40, 45, 0.14);
    transform: translateY(-3px);
  }
  /* Touch screens have no hover, so give the resting card a deeper shadow
     for more separation on mobile. */
  @media (max-width: 760px) {
    box-shadow: 0 4px 16px rgba(20, 40, 45, 0.16);
  }
`;
const ImgWrap = styled.div`
  position: relative;
  width: 100%;
  padding-top: 100%;
  background: #ffffff;
  border-bottom: 1px solid #f0f3f4;
`;
const Img = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 12px;
  display: block;
`;
const VendorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #4a5b61;
  margin-bottom: 5px;
  min-width: 0;
`;
const VendorName = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  a:hover &,
  a:focus-visible & {
    text-decoration: underline;
    text-underline-offset: 2px;
    color: #1f2a2e;
  }
`;
const Dot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-block;
  background: ${(p) => p.color};
`;
const SoldOut = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.66);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1px;
  color: #b03a3a;
  pointer-events: none;
`;
const Body = styled.div`
  padding: 10px 12px 12px;
  display: flex;
  flex-direction: column;
  flex: 1;
`;
const Cat = styled.div`
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6b7a80;
  font-weight: 600;
`;
const Name = styled.div`
  font-size: 13.5px;
  font-weight: 600;
  color: #732509;
  margin: 3px 0 5px;
  line-height: 1.3;
  min-height: 34px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
  word-break: break-word;
`;
const RateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #6b7a80;
  margin-bottom: 7px;
  min-height: 16px;
`;
const PriceBlock = styled.div`
  margin-top: auto;
  margin-bottom: 9px;
`;
const Price = styled.div`
  font-size: ${(p) => (p.$quote ? "12px" : "15px")};
  font-weight: 800;
  color: ${(p) => (p.$quote ? "#6b7a80" : "#0f9aa8")};
`;
const Pack = styled.div`
  font-size: 11px;
  color: #6b7a80;
  margin-top: 2px;
`;
const ActionBtn = styled.button`
  width: 100%;
  border: 0;
  border-radius: 9px;
  height: 38px;
  padding: 0 10px;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  color: #ffffff;
  background: ${ACTION_GREEN};
  transition: background 0.15s ease;
  &:hover:not(:disabled) {
    background: ${ACTION_GREEN_DARK};
  }
  &:focus-visible {
    outline: 3px solid #9fd8ad;
    outline-offset: 2px;
  }
  &:disabled {
    cursor: not-allowed;
    color: #7a878c;
    background: #eef1f2;
  }
  &[aria-busy="true"] {
    cursor: progress;
    background: ${ACTION_GREEN_DARK};
  }
`;
const Stepper = styled.div`
  display: grid;
  grid-template-columns: 38px 1fr 38px;
  align-items: center;
  height: 38px;
  border-radius: 9px;
  background: #e9f6ee;
  border: 1px solid #bfe6cd;
  overflow: hidden;
`;
const StepBtn = styled.button`
  height: 100%;
  border: 0;
  color: #ffffff;
  background: ${ACTION_GREEN};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: ${ACTION_GREEN_DARK};
  }
  &:focus-visible {
    outline: 3px solid #9fd8ad;
    outline-offset: -3px;
  }
  &:disabled {
    cursor: not-allowed;
    background: #9fbfa5;
  }
`;
const StepCount = styled.div`
  text-align: center;
  font-size: 12.5px;
  font-weight: 800;
  color: #037d30;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 4px;
`;
const InCartNote = styled.div`
  margin-top: 6px;
  text-align: center;
  font-size: 11.5px;
  font-weight: 700;
  color: #037d30;
`;

const svgProps = {
  width: 16,
  height: 16,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2.2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": "true",
};
const CartIcon = () => (
  <svg {...svgProps}>
    <circle cx="9" cy="21" r="1" />
    <circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
  </svg>
);
const OptionsIcon = () => (
  <svg {...svgProps}>
    <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" />
  </svg>
);
const QuoteIcon = () => (
  <svg {...svgProps}>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);
const MinusIcon = () => (
  <svg {...svgProps}>
    <path d="M5 12h14" />
  </svg>
);
const PlusIcon = () => (
  <svg {...svgProps}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);
const ACTION_ICON = {
  [CATALOG_ACTIONS.ADD]: CartIcon,
  [CATALOG_ACTIONS.CHOOSE]: OptionsIcon,
  [CATALOG_ACTIONS.QUOTE]: QuoteIcon,
};

const ProductCard = ({ product }) => {
  const sale = useCatalogProductCart(product);

  const {
    name, image, product_image, vendorType, vendorName,
    countInStock, ratingAverage = 0, ratingCount = 0, measurements, unit_id, categoryNames,
  } = product || {};

  const src = image || product_image;
  const inStock = Number(countInStock) > 0;
  const accent = VT_COLOR[vendorType] || "#6b7a80";
  const catLabel = Array.isArray(categoryNames) && categoryNames.length ? categoryNames[0] : "";
  const shownVendor = vendorName || vendorTypeLabel(vendorType);
  const ActionIcon = ACTION_ICON[sale.action] || null;

  // The product's own page, as a real link for search engines and new tabs.
  const toProduct = (children, style) =>
    sale.productPath ? (
      <CrawlLink to={sale.productPath} style={style}>
        {children}
      </CrawlLink>
    ) : (
      children
    );

  return (
    <Card>
      <ImgWrap title={name}>
        {src
          ? toProduct(
              <Img
                src={optimizedImage(src, "card")}
                srcSet={optimizedSrcSet(src, "card")}
                sizes="(max-width: 600px) 45vw, 240px"
                loading="lazy"
                decoding="async"
                alt={name || "Product"}
              />,
              { display: "block" },
            )
          : null}
        {!inStock && <SoldOut>SOLD OUT</SoldOut>}
      </ImgWrap>

      <Body>
        <VendorRow>
          <Dot color={accent} />
          {sale.storePath ? (
            <CrawlLink
              to={sale.storePath}
              title={`Visit ${shownVendor}'s store`}
              style={{ minWidth: 0, display: "flex" }}
            >
              <VendorName>{shownVendor}</VendorName>
            </CrawlLink>
          ) : (
            <VendorName title={shownVendor}>{shownVendor}</VendorName>
          )}
        </VendorRow>
        {catLabel ? <Cat>{catLabel}</Cat> : null}
        <Name>{toProduct(name)}</Name>

        <RateRow>
          {ratingCount > 0 ? (
            <>
              <Stars value={ratingAverage} />
              <span>
                {Number(ratingAverage).toFixed(1)} ({ratingCount})
              </span>
            </>
          ) : (
            <span style={{ color: "#9aa7ac" }}>No ratings yet</span>
          )}
        </RateRow>

        <PriceBlock>
          <Price $quote={isQuoteOnly(product)}>{priceLabel(product, formatCurrency)}</Price>
          {measurements || unit_id?.unit ? (
            <Pack>
              {measurements} {unit_id?.unit || ""}
              {sale.minQty > 1 ? ` · Min. order ${sale.minQty}` : ""}
            </Pack>
          ) : sale.minQty > 1 ? (
            <Pack>Min. order {sale.minQty}</Pack>
          ) : null}
        </PriceBlock>

        {sale.steppable ? (
          <Stepper role="group" aria-label={`${name} in your cart`}>
            <StepBtn
              type="button"
              onClick={sale.decrease}
              aria-label={sale.quantity - 1 < sale.minQty ? `Remove ${name} from cart` : `One less ${name}`}
            >
              <MinusIcon />
            </StepBtn>
            <StepCount aria-live="polite">{sale.quantity} in cart</StepCount>
            <StepBtn
              type="button"
              onClick={sale.increase}
              disabled={!sale.canIncrease}
              aria-label={`One more ${name}`}
              title={sale.canIncrease ? undefined : "No more in stock"}
            >
              <PlusIcon />
            </StepBtn>
          </Stepper>
        ) : (
          <>
            <ActionBtn
              type="button"
              onClick={sale.add}
              disabled={sale.action === CATALOG_ACTIONS.UNAVAILABLE}
              aria-busy={sale.busy ? "true" : undefined}
              aria-label={
                sale.action === CATALOG_ACTIONS.ADD ? `Add ${name} to cart` : `${sale.label}: ${name}`
              }
            >
              {ActionIcon ? <ActionIcon /> : null}
              {sale.busy ? "Adding..." : sale.label}
            </ActionBtn>
            {sale.quantity > 0 && sale.action !== CATALOG_ACTIONS.ADD ? (
              <InCartNote>{sale.quantity} in cart</InCartNote>
            ) : null}
          </>
        )}
      </Body>
    </Card>
  );
};

ProductCard.propTypes = { product: PropTypes.object };

export default ProductCard;
